import React, { useEffect, useState } from 'react';
import { Select, UISelect } from '~/components/common/forms';
import { Translate, useTranslate } from '~/i18n';

import Pagination from '~/components/common/pagination';
import RatingStars from '~/components/common/rating-stars';
import ReviewItem from './sections/review-item';
import ReviewOverview from './sections/review-overview';
import Search from '~/components/common/search';
import { Section } from '~/components/common/section';
import { SubmitLoader } from '~/components/common/loaders/submit';
import { WarningOutlineIcon } from '~/svgs/marketplace';
import { useAuth } from '~/auth';
import useReviewsList from '~/apis/reviews/useReviewsList.api';

const Reviews = () => {
   const { translate } = useTranslate();
   const { user } = useAuth();
   const [selectedPlatform, setSelectedPlatform] = useState('');
   const [searchValue, setSearchValue] = useState<string[] | null>(null);
   const [sortBy, setSortBy] = useState('');
   const [pageNumber, setPageNumber] = useState(1);
   const { isLoading, data, refetch } = useReviewsList({
      searchValue: searchValue || undefined,
      venue_visibility: selectedPlatform || undefined,
      sortBy: sortBy || undefined,
      pageNumber,
   });

   const platformOptions = [
      { value: '', label: translate({ id: 'productsList:all products' }) },
      { value: 'vs', label: translate({ id: 'reviews:products on vs' }) },
      { value: 'marketplace', label: translate({ id: 'reviews:products on b2b' }) },
   ];

   const sortOptions = [
      { value: 'asc', label: translate({ id: 'reviews:date asc' }) },
      { value: 'desc', label: translate({ id: 'reviews:date desc' }) },
      { value: 'rating_asc', label: translate({ id: 'reviews:rating asc' }) },
      { value: 'rating_desc', label: translate({ id: 'reviews:rating desc' }) },
   ];

   useEffect(() => {
      setPageNumber(1);
   }, [sortBy]);

   return (
      <>
         <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 px-3 py-5 md:p-0 md:pb-3 mt-0 md:mt-8 -mx-2 md:mx-0 bg-white md:bg-transparent">
            <div className="flex flex-col w-full md:w-1/3">
               <span className="mb-2 text-black font-bold text-lg">
                  <Translate id="reviews:reviews title" />
               </span>
               <p className="text-gray-500 text-sm">
                  <Translate id="reviews:reviews description" />
               </p>
            </div>
            <div className="flex  w-full md:w-3/4 justify-end gap-5">
               <div className="flex flex-col">
                  <span className="font-semibold text-sm">
                     <Translate id="reviews:total reviews" />
                  </span>
                  <span className="font-semibold text-4xl">
                     {user?.rating_analytics?.total_reviews || 0}
                  </span>
                  <span className="text-sm text-grey-300">
                     {user?.rating_analytics?.total_reviews > 0 ? (
                        <Translate
                           id="reviews:rating growth"
                           values={{ amount: user?.rating_analytics?.growth_percentage || '0' }}
                        />
                     ) : (
                        <Translate id="reviews:no reviews" />
                     )}
                  </span>
               </div>
               <div className="flex flex-col border-x px-2 md:px-6">
                  <span className="font-semibold text-sm">
                     <Translate id="reviews:average rating" />
                  </span>
                  <span className="flex gap-2 items-center font-semibold text-4xl">
                     <span>
                        {user?.rating_analytics?.average_rating > 0
                           ? user?.rating_analytics?.average_rating.toFixed(1)
                           : 0.0}
                     </span>{' '}
                     <RatingStars
                        rating_value={user?.rating_analytics?.average_rating || 0}
                        rating_max_value={5}
                     />
                  </span>
                  <span className="font-semibold text-[13px] text-grey-300">
                     <Translate id="reviews:average rating this year" />
                  </span>
               </div>
               <ReviewOverview
                  total_reviews={user?.rating_analytics?.total_reviews}
                  ratings_breakdown={user?.rating_analytics?.total_reviews_breakdown}
               />
            </div>
         </div>
         <div className="flex flex-col justify-between w-full my-3 lg:flex-row lg:space-x-4 rtl:lg:space-x-reverse">
            <Select
               groupClassName="w-full lg:w-52 font-semibold"
               className="focus:!ring-transparent focus:!shadow-none"
               onChange={({ target: { value } }: any) => {
                  setSelectedPlatform(value);
               }}
               defaultValue={selectedPlatform}>
               {platformOptions?.map((item, index) => {
                  return (
                     <option key={index} value={item.value}>
                        {item.label}
                     </option>
                  );
               })}
            </Select>
            <div className="w-full lg:w-1/2">
               <Search
                  onSearch={(text: string[]) => setSearchValue(text)}
                  placeholder={translate({ id: 'reviews:search reviews placeholder' })}
               />
            </div>
            <div className="w-full lg:w-52">
               <UISelect
                  onChange={(val: any) => setSortBy(val?.value || null)}
                  options={sortOptions}
                  groupClassName="!mb-0"
                  placeholder={<Translate id="bookings:sort by" />}
                  value={sortOptions.filter((item: any) => item.value === sortBy)}
                  isClearable={true}
                  instanceId="type-search"
               />
            </div>
         </div>
         {isLoading ? (
            <div className="flex justify-center">
               <SubmitLoader />
            </div>
         ) : data?.length ? (
            <Section className="mb-10 shadow-lg">
               <div className="w-full flex flex-col py-3 px-10">
                  {data?.map((review: any) => (
                     <ReviewItem key={review.id} review={review} refetchReviews={refetch} />
                  ))}
               </div>
            </Section>
         ) : (
            <div className="flex justify-center pb-5">
               <div className="flex flex-col items-center text-grey-200 w-full md:w-1/2">
                  <WarningOutlineIcon className="mt-10 mb-5" />
                  <span className="text-center">
                     <Translate id="reviews:no reviews description" />
                  </span>
               </div>
            </div>
         )}
         {data && data?.total > data.per_page && (
            <div className="flex w-full p-2">
               <Pagination
                  back={() => setPageNumber(prev => prev - 1)}
                  next={() => setPageNumber(prev => prev + 1)}
                  currentPage={data.curr_page}
                  lastPage={data.last_page}
                  numberOfElementsInOnePage={data.per_page}
                  totalElements={data.total}
               />
            </div>
         )}
      </>
   );
};

export default Reviews;
